import { formatDateOnly } from '@hrms/ui';
import type { AssignTrainingTaskBody, TrainingAssignmentDue, TrainingAudience } from '@hrms/shared';

/**
 * Pure rules behind the Assign drawer (training redesign PLAN §3.3, R11) — kept out of the component
 * so the mapping onto `assignTrainingTaskBodySchema` is unit-tested rather than eyeballed.
 *
 * The drawer lets HR mix people, departments, locations, legal entities and Everyone in ONE picker,
 * but the contract's `audience` is a discriminated union with exactly one kind per request. Rather
 * than restrict the picker, the drawer sends one request per kind, sharing an idempotency-key prefix
 * with a per-kind suffix. The result is the union HR picked: the server skips anyone a previous
 * request already assigned (`already_assigned`), so overlapping groups never double-assign.
 */

export type AudienceChipKind = 'everyone' | 'person' | 'department' | 'location' | 'legal_entity';

export type AudienceChip =
  | { kind: 'everyone' }
  | { kind: 'person' | 'department' | 'location' | 'legal_entity'; id: string; name: string; subtitle?: string | null };

/** The contract caps a people audience at 200 ids. */
export const MAX_PEOPLE_PER_ASSIGN = 200;
export const DEFAULT_DUE_TIME = '18:00';
export const DEFAULT_WITHIN_DAYS = 14;
export const DEFAULT_NEW_JOINER_DAYS = 30;

export function chipKey(chip: AudienceChip): string {
  return chip.kind === 'everyone' ? 'everyone' : `${chip.kind}:${chip.id}`;
}

/**
 * Adds a chip. "Everyone" already covers every group, so choosing it replaces the other chips, and
 * choosing any group after it drops "Everyone" — the picker never shows a contradiction.
 */
export function addAudienceChip(chips: readonly AudienceChip[], chip: AudienceChip): AudienceChip[] {
  if (chip.kind === 'everyone') return [chip];
  const key = chipKey(chip);
  const rest = chips.filter((c) => c.kind !== 'everyone' && chipKey(c) !== key);
  if (chip.kind === 'person' && rest.filter((c) => c.kind === 'person').length >= MAX_PEOPLE_PER_ASSIGN) {
    return [...chips];
  }
  return [...rest, chip];
}

export function removeAudienceChip(chips: readonly AudienceChip[], key: string): AudienceChip[] {
  return chips.filter((c) => chipKey(c) !== key);
}

/**
 * The audiences to send, one per kind. "Only new joiners" is its own audience kind
 * (`probation_cohort`) that the server cannot intersect with a group, so while it is on the picked
 * chips are not used — the drawer says so on screen.
 */
export function buildAudiences(
  chips: readonly AudienceChip[],
  newJoiners: { on: boolean; days: number },
): TrainingAudience[] {
  if (newJoiners.on) {
    return isValidJoinedWithinDays(newJoiners.days)
      ? [{ kind: 'probation_cohort', joinedWithinDays: newJoiners.days }]
      : [];
  }
  if (chips.some((c) => c.kind === 'everyone')) return [{ kind: 'all_active' }];

  const idsOf = (kind: 'person' | 'department' | 'location' | 'legal_entity') =>
    chips.flatMap((c) => (c.kind !== 'everyone' && c.kind === kind ? [c.id] : []));
  const out: TrainingAudience[] = [];
  const people = idsOf('person');
  if (people.length > 0) out.push({ kind: 'people', personIds: people.slice(0, MAX_PEOPLE_PER_ASSIGN) });
  const departments = idsOf('department');
  if (departments.length > 0) out.push({ kind: 'department', ids: departments });
  const locations = idsOf('location');
  if (locations.length > 0) out.push({ kind: 'location', ids: locations });
  const entities = idsOf('legal_entity');
  if (entities.length > 0) out.push({ kind: 'legal_entity', ids: entities });
  return out;
}

export function isValidJoinedWithinDays(days: number): boolean {
  return Number.isInteger(days) && days >= 1 && days <= 180;
}

/** The query `trainingAssignments.audiencePreview` takes for one audience. */
/**
 * Picked people need no server round trip to be counted: the count IS the picks, and their names are
 * on the chips. (The server also drops anyone out of scope or already assigned when it assigns, which
 * the drawer already says: "anyone who already has it is skipped".) Sending them through a URL was
 * the failure — the list never arrived, the count read 0, and Assign stayed off. Every other kind is
 * counted by the server.
 */
export function localAudienceCount(audience: TrainingAudience): number | null {
  return audience.kind === 'people' ? audience.personIds.length : null;
}

export function audiencePreviewQuery(taskId: string, audience: TrainingAudience) {
  switch (audience.kind) {
    case 'people':
      return { taskId, kind: audience.kind, personIds: audience.personIds };
    case 'probation_cohort':
      return { taskId, kind: audience.kind, joinedWithinDays: audience.joinedWithinDays };
    default:
      return audience.ids && audience.ids.length > 0
        ? { taskId, kind: audience.kind, ids: audience.ids }
        : { taskId, kind: audience.kind };
  }
}

// ---- Due ------------------------------------------------------------------------------------------

export type DueChoice =
  | { mode: 'within'; days: number }
  | { mode: 'date'; date: string; time: string }
  | { mode: 'none' };

/**
 * Today's calendar date for the person assigning, as `YYYY-MM-DD`. This is the HR user's own day —
 * the same thing a date picker hands them — not "what day it is for the employee": the server turns
 * the resulting due date + time into an instant in each employee's own location timezone
 * (CLAUDE.md invariant 3).
 */
export function assignerToday(now: Date = new Date()): string {
  const y = now.getFullYear();
  const m = String(now.getMonth() + 1).padStart(2, '0');
  const d = String(now.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

export function addDaysToDateOnly(date: string, days: number): string {
  const [y, m, d] = date.split('-').map(Number);
  const utc = new Date(Date.UTC(y ?? 1970, (m ?? 1) - 1, d ?? 1));
  utc.setUTCDate(utc.getUTCDate() + days);
  return utc.toISOString().slice(0, 10);
}

export function isValidWithinDays(days: number, newJoinersOnly: boolean): boolean {
  // `relative_to_join` caps at 90 days; a fixed date has no cap in the contract, a year is plenty.
  return Number.isInteger(days) && days >= 1 && days <= (newJoinersOnly ? 90 : 365);
}

/**
 * What the people assigned NOW are due by. "Within N days" means N days from today for an existing
 * employee (a fixed date), but N days from joining for a new-joiner audience (`relative_to_join`) —
 * the contract's `relative_to_join` counts from the joining date, which for someone hired years ago
 * would already be in the past.
 */
export function buildDue(
  choice: DueChoice,
  opts: { today: string; newJoinersOnly: boolean },
): TrainingAssignmentDue | null {
  switch (choice.mode) {
    case 'none':
      return { kind: 'none' };
    case 'date':
      return /^\d{4}-\d{2}-\d{2}$/.test(choice.date) && /^([01]\d|2[0-3]):[0-5]\d$/.test(choice.time)
        ? { kind: 'fixed', dueDate: choice.date, dueTime: choice.time }
        : null;
    case 'within':
      if (!isValidWithinDays(choice.days, opts.newJoinersOnly)) return null;
      return opts.newJoinersOnly
        ? { kind: 'relative_to_join', days: choice.days, dueTime: DEFAULT_DUE_TIME }
        : {
            kind: 'fixed',
            dueDate: addDaysToDateOnly(opts.today, choice.days),
            dueTime: DEFAULT_DUE_TIME,
          };
  }
}

/** "Also assign to people who join later" works with "within N days" or no due date; a single fixed
 * date means nothing to someone who joins after it (the server's auto-enrolment skips such batches). */
export function futureJoinersAllowed(choice: DueChoice): boolean {
  return choice.mode !== 'date';
}

// ---- Requests -------------------------------------------------------------------------------------

export interface AssignRequest {
  body: AssignTrainingTaskBody;
  idempotencyKey: string;
  /** `future` = the open batch that only exists to enrol people who join later. */
  role: 'now' | 'future';
}

/**
 * Every request the drawer sends, in order. Usually one per audience kind. With "Also assign to
 * people who join later" on a group audience and a "within N days" deadline, the current people need
 * a fixed date while later joiners need N days from THEIR joining date — one batch cannot carry both,
 * so a second, `future` request follows with `relative_to_join` + auto-enrol: everyone already
 * assigned by the first is skipped by the server, and the batch stays open for new joiners.
 */
export function buildAssignRequests(input: {
  taskId: string;
  audiences: readonly TrainingAudience[];
  due: TrainingAssignmentDue;
  choice: DueChoice;
  isMandatory: boolean;
  futureJoiners: boolean;
  keyPrefix: string;
}): AssignRequest[] {
  const out: AssignRequest[] = [];
  for (const audience of input.audiences) {
    const key = `${input.keyPrefix}-${audience.kind}`;
    const base = { taskId: input.taskId, audience, due: input.due, isMandatory: input.isMandatory };
    // A named list of people never grows, so auto-enrolment means nothing for it.
    const wantsFuture = input.futureJoiners && audience.kind !== 'people' && futureJoinersAllowed(input.choice);
    if (!wantsFuture) {
      out.push({ body: base, idempotencyKey: key, role: 'now' });
      continue;
    }
    if (input.due.kind === 'fixed' && input.choice.mode === 'within') {
      out.push({ body: { ...base, autoEnrolNewJoiners: false }, idempotencyKey: key, role: 'now' });
      out.push({
        body: {
          ...base,
          due: { kind: 'relative_to_join', days: Math.min(input.choice.days, 90), dueTime: DEFAULT_DUE_TIME },
          autoEnrolNewJoiners: true,
        },
        idempotencyKey: `${key}-future`,
        role: 'future',
      });
      continue;
    }
    out.push({ body: { ...base, autoEnrolNewJoiners: true }, idempotencyKey: key, role: 'now' });
  }
  return out;
}

// ---- Results --------------------------------------------------------------------------------------

export type AssignSkipReason = 'out_of_scope' | 'already_assigned' | 'inactive' | 'non_working_due_day';

export type AssignOutcome =
  | { role: 'now' | 'future'; kind: 'created'; createdCount: number; skipped: { personId: string; reason: AssignSkipReason }[] }
  | { role: 'now' | 'future'; kind: 'background'; batchId: string };

export interface AssignSummary {
  created: number;
  /** Distinct people skipped, by reason. A person in two picked groups counts once. */
  skipped: Partial<Record<AssignSkipReason, number>>;
  skippedTotal: number;
  /** At least one request was large enough to run in the background (202). */
  background: boolean;
}

/** Folds the per-request answers into what the drawer says. `future` requests only open the batch for
 * later joiners — whatever they skip (everyone, by design) is not news to HR. */
export function summarizeAssignOutcomes(outcomes: readonly AssignOutcome[]): AssignSummary {
  let created = 0;
  let background = false;
  const reasonByPerson = new Map<string, AssignSkipReason>();
  for (const outcome of outcomes) {
    if (outcome.kind === 'background') {
      if (outcome.role === 'now') background = true;
      continue;
    }
    created += outcome.createdCount;
    if (outcome.role === 'future') continue;
    for (const skip of outcome.skipped) {
      if (!reasonByPerson.has(skip.personId)) reasonByPerson.set(skip.personId, skip.reason);
    }
  }
  const skipped: Partial<Record<AssignSkipReason, number>> = {};
  for (const reason of reasonByPerson.values()) skipped[reason] = (skipped[reason] ?? 0) + 1;
  return { created, skipped, skippedTotal: reasonByPerson.size, background };
}

export const SKIP_REASON_LABEL: Record<AssignSkipReason, string> = {
  already_assigned: 'already have it',
  out_of_scope: 'are outside the people you can assign to',
  inactive: 'are not active employees',
  non_working_due_day: 'would be due on a day they do not work',
};

export function skipSummaryText(summary: AssignSummary): string {
  const parts = (Object.keys(SKIP_REASON_LABEL) as AssignSkipReason[])
    .filter((reason) => (summary.skipped[reason] ?? 0) > 0)
    .map((reason) => `${summary.skipped[reason]} ${SKIP_REASON_LABEL[reason]}`);
  return parts.join(', ');
}

/** "48 people" / "Up to 60 people" — a sum over several groups can count someone twice. */
export function audienceCountLabel(counts: readonly number[]): string {
  const total = counts.reduce((sum, n) => sum + n, 0);
  const noun = total === 1 ? 'person' : 'people';
  return counts.length > 1 ? `Up to ${total} ${noun}` : `${total} ${noun}`;
}

export function dueSummaryText(due: TrainingAssignmentDue | null): string {
  if (!due) return '';
  if (due.kind === 'none') return 'No due date — they can take it any time.';
  if (due.kind === 'relative_to_join') {
    return `Due ${due.days} day${due.days === 1 ? '' : 's'} after each person joined, by ${formatTimeOfDay(due.dueTime)} their time.`;
  }
  return `Due by ${formatTimeOfDay(due.dueTime)} on ${formatDateOnly(due.dueDate)}, in each person's own time zone.`;
}

/** `18:00` → `6:00 pm`. A wall-clock time, not an instant, so no time zone is involved. */
export function formatTimeOfDay(hhmm: string): string {
  const [h = 0, m = 0] = hhmm.split(':').map(Number);
  const suffix = h >= 12 ? 'pm' : 'am';
  const hour = h % 12 === 0 ? 12 : h % 12;
  return `${hour}:${String(m).padStart(2, '0')} ${suffix}`;
}
